import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useRef, useState, useMemo, useEffect } from "react";
import { Play, ArrowUpRight, Upload, X } from "lucide-react";
import { useI18n } from "../../lib/i18n";

const items = [
    {
        id: "podcast-photo",
        cat: "Podcast",
        title: "Podcast · Photo",
        meta: "Studio photography · Placeholder",
        image: "",
        videoUrl: "#",
        placeholder: true,
        span: "md:col-span-4",
    },
    {
        id: "drone-footage",
        cat: "Drone",
        title: "Hospitality Aerial Film",
        meta: "Drone · 4K · Cinematic",
        image: "",
        videoSrc:
            "https://customer-assets-7cd3h4nn.emergentagent.net/job_ftak-productions/artifacts/k3qi6dpc_Hospitality%20Aerial%20Film.mp4",
        videoUrl: "#",
        placeholder: false,
        span: "md:col-span-4",
    },
    {
        id: "business-video",
        cat: "Business",
        title: "Retail Brand Film",
        meta: "Commercial · Brand Storytelling",
        image: "",
        videoSrc:
            "https://customer-assets-7cd3h4nn.emergentagent.net/job_ftak-productions/artifacts/nfcl2rtt_Retail%20Brand%20Film%CF%83%CF%89%CF%83%CF%84%CE%BF.mp4",
        videoUrl: "#",
        placeholder: false,
        span: "md:col-span-4",
    },
];

const categories = ["All"];

/* Empty-state SVG for placeholders — pure CSS, no external asset */
function PlaceholderVisual({ id }) {
    return (
        <div
            aria-hidden
            className="absolute inset-0"
            style={{
                background:
                    "radial-gradient(1200px 400px at 30% 0%, rgba(229,91,19,0.10), transparent 50%), radial-gradient(600px 400px at 100% 100%, rgba(229,91,19,0.05), transparent 50%), linear-gradient(180deg, #0d0d0d 0%, #050505 100%)",
            }}
        >
            {/* Diagonal grain lines */}
            <svg
                width="100%"
                height="100%"
                className="opacity-[0.06]"
                preserveAspectRatio="none"
            >
                <defs>
                    <pattern
                        id={`p-${id}`}
                        width="24"
                        height="24"
                        patternUnits="userSpaceOnUse"
                        patternTransform="rotate(35)"
                    >
                        <line
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="24"
                            stroke="white"
                            strokeWidth="0.6"
                        />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill={`url(#p-${id})`} />
            </svg>
        </div>
    );
}

function PortfolioTile({ item, index, onOpen }) {
    const { t } = useI18n();
    const trItem = t.portfolio.items?.[item.id];
    const ref = useRef(null);
    const videoRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [30, -30]);
    const isPlaceholder = item.placeholder || (!item.image && !item.videoSrc);
    const hasVideo = !!item.videoSrc;
    const displayTitle = trItem?.title || item.title;
    const displayMeta = trItem?.meta || item.meta;
    const displayCat = t.portfolio.categories?.[item.cat] || item.cat;

    const handleClick = (e) => {
        if (hasVideo) {
            e.preventDefault();
            onOpen(item);
        }
    };

    return (
        <motion.a
            ref={ref}
            href={hasVideo ? "#" : item.videoUrl || "#contact"}
            onClick={handleClick}
            onMouseEnter={() => {
                if (hasVideo && videoRef.current) {
                    videoRef.current.play().catch(() => {});
                }
            }}
            onMouseLeave={() => {
                if (hasVideo && videoRef.current) {
                    videoRef.current.pause();
                    videoRef.current.currentTime = 0.1;
                }
            }}
            layout
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{
                duration: 0.9,
                delay: index * 0.05,
                ease: [0.16, 1, 0.3, 1],
            }}
            className={`portfolio-item card-lift block relative ${item.span} h-[420px] md:h-[520px] group cursor-pointer`}
            data-testid={`portfolio-item-${item.id}`}
            data-placeholder={isPlaceholder ? "true" : "false"}
        >
            {/* Visual layer */}
            <motion.div
                style={{ y }}
                className="absolute inset-0 -top-6 -bottom-6"
            >
                {hasVideo ? (
                    <video
                        ref={videoRef}
                        src={item.videoSrc}
                        muted
                        playsInline
                        autoPlay
                        loop
                        preload="auto"
                        className="w-full h-full object-cover opacity-95"
                    />
                ) : isPlaceholder ? (
                    <PlaceholderVisual id={item.id} />
                ) : (
                    <img
                        src={item.image}
                        alt={displayTitle}
                        className="w-full h-full object-cover opacity-90"
                        loading="lazy"
                    />
                )}
            </motion.div>

            {/* Overlay */}
            <div className="portfolio-overlay">
                <div>
                    <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#E55B13] mb-2">
                        {displayCat}
                    </div>
                    <h3 className="font-heading text-2xl md:text-3xl font-light tracking-[-0.02em] text-white leading-tight max-w-md">
                        {displayTitle}
                    </h3>
                    <p className="mt-2 font-mono text-[11px] tracking-[0.2em] text-white/50 uppercase">
                        {displayMeta}
                    </p>
                </div>
                <div className="w-14 h-14 rounded-full bg-white/10 border border-white/20 backdrop-blur-md flex items-center justify-center text-white group-hover:bg-[#E55B13] group-hover:border-[#E55B13] transition-all duration-500 group-hover:scale-110">
                    {isPlaceholder ? (
                        <Upload size={16} />
                    ) : (
                        <Play size={16} fill="currentColor" />
                    )}
                </div>
            </div>

            {/* Number */}
            <div className="absolute top-6 left-6 font-mono text-[11px] tracking-[0.28em] text-white/60">
                / {String(index + 1).padStart(2, "0")}
            </div>

            {/* Placeholder / Live chip */}
            {isPlaceholder ? (
                <div className="absolute top-6 right-6 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/50 border border-white/10 backdrop-blur-md text-[10px] font-mono tracking-[0.22em] uppercase text-white/70">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E55B13]" />
                    {t.portfolio.uploadMedia}
                </div>
            ) : (
                hasVideo && (
                    <div className="absolute top-6 right-6 flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E55B13]/90 border border-[#E55B13] text-[10px] font-mono tracking-[0.22em] uppercase text-black">
                        <span className="w-1.5 h-1.5 rounded-full bg-black" />
                        Watch Film
                    </div>
                )
            )}
        </motion.a>
    );
}

export default function Portfolio() {
    const { t } = useI18n();
    const s = t.portfolio;
    const [filter, setFilter] = useState("All");
    const [activeVideo, setActiveVideo] = useState(null);
    const filtered = useMemo(
        () =>
            filter === "All"
                ? items
                : items.filter((i) => i.cat === filter),
        [filter],
    );

    // Lock body scroll & handle ESC
    useEffect(() => {
        if (!activeVideo) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e) => {
            if (e.key === "Escape") setActiveVideo(null);
        };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener("keydown", onKey);
        };
    }, [activeVideo]);

    return (
        <section
            id="portfolio"
            className="section relative bg-[#050505]"
            data-testid="portfolio-section"
        >
            <div className="container-x">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-10">
                    <div>
                        <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.28em] text-[#E55B13] mb-6">
                            <span className="w-8 h-px bg-[#E55B13]" />
                            {s.eyebrow}
                        </div>
                        <h2 className="font-heading text-4xl md:text-6xl lg:text-7xl font-light tracking-[-0.03em] leading-[1.02] max-w-4xl">
                            {s.titlePre}
                            <span className="italic font-extralight text-white/60">
                                {s.titleAccent}
                            </span>
                            {s.titlePost}
                        </h2>
                    </div>
                    <a
                        href="#contact"
                        className="btn-ghost inline-flex items-center gap-2 self-start md:self-auto"
                        data-testid="portfolio-cta"
                    >
                        {s.cta}
                        <ArrowUpRight size={14} />
                    </a>
                </div>

                <motion.div
                    layout
                    className="grid grid-cols-1 md:grid-cols-12 gap-5 mt-4"
                    data-testid="portfolio-grid"
                >
                    {filtered.map((item, i) => (
                        <PortfolioTile
                            key={item.id}
                            item={item}
                            index={i}
                            onOpen={setActiveVideo}
                        />
                    ))}
                </motion.div>

                <div className="mt-12 rounded-3xl border border-white/[0.08] bg-white/[0.02] p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
                    <div className="w-11 h-11 rounded-2xl bg-[#E55B13]/15 border border-[#E55B13]/40 flex items-center justify-center text-[#E55B13]">
                        <Upload size={18} strokeWidth={1.6} />
                    </div>
                    <div className="flex-1">
                        <div className="font-heading text-lg md:text-xl font-medium text-white tracking-[-0.01em]">
                            {s.editableNote.title}
                        </div>
                        <p className="mt-1 font-body text-sm text-white/55 leading-relaxed max-w-3xl">
                            {s.editableNote.body}
                        </p>
                    </div>
                </div>
            </div>

            {/* Premium video modal */}
            <AnimatePresence>
                {activeVideo && (
                    <motion.div
                        key="video-modal"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed inset-0 z-[70] flex items-center justify-center p-4 md:p-10 bg-black/85 backdrop-blur-md"
                        onClick={() => setActiveVideo(null)}
                        data-testid="portfolio-video-modal"
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.94, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.96, y: 20 }}
                            transition={{
                                duration: 0.55,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative w-full max-w-6xl rounded-2xl md:rounded-[28px] overflow-hidden border border-white/10 bg-[#050505] shadow-[0_50px_140px_-20px_rgba(229,91,19,0.35)]"
                        >
                            <button
                                onClick={() => setActiveVideo(null)}
                                aria-label="Close video"
                                data-testid="portfolio-video-close"
                                className="absolute top-3 right-3 md:top-4 md:right-4 z-10 w-10 h-10 md:w-11 md:h-11 rounded-full bg-black/60 border border-white/15 backdrop-blur-md text-white hover:bg-[#E55B13] hover:border-[#E55B13] transition-colors flex items-center justify-center"
                            >
                                <X size={18} />
                            </button>

                            <div className="relative aspect-video bg-black">
                                <video
                                    src={activeVideo.videoSrc}
                                    controls
                                    autoPlay
                                    playsInline
                                    muted={!!activeVideo.mutedInModal}
                                    className="w-full h-full object-contain bg-black"
                                />
                            </div>

                            <div className="p-5 md:p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-t border-white/[0.06] bg-[#0a0a0a]">
                                <div>
                                    <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-[#E55B13]">
                                        {activeVideo.cat}
                                    </div>
                                    <div className="mt-1 font-heading text-xl md:text-2xl font-light tracking-[-0.02em] text-white">
                                        {activeVideo.title}
                                    </div>
                                </div>
                                <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/50">
                                    {activeVideo.meta}
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
